import { useEffect, useRef, useState } from 'react';
import { COMPANY, NAV_ITEMS } from '@/mocks/home';

function Logo() {
  return (
    <a href="#home" className="flex items-center gap-2 group">
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-500 text-background-50">
        <i className="ri-bubble-chart-fill text-2xl"></i>
      </span>
      <span className="leading-none">
        <span className="block font-heading text-xl font-extrabold tracking-tight text-primary-700">
          Crystal<span className="text-accent-600">Clean</span>
        </span>
        <span className="block text-[11px] font-medium uppercase tracking-[0.18em] text-secondary-700">
          Fort Myers
        </span>
      </span>
    </a>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const closeTimer = useRef<number | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleEnter = (label: string) => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    setOpenDropdown(label);
  };

  const handleLeave = () => {
    closeTimer.current = window.setTimeout(() => setOpenDropdown(null), 140);
  };

  return (
    <header className="w-full">
      {/* Top utility bar */}
      <div className="hidden bg-secondary-100 md:block">
        <div className="mx-auto flex h-10 max-w-[1280px] items-center justify-between px-4 md:px-6">
          <div className="flex items-center gap-2 text-xs font-medium text-secondary-900">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-accent-500 text-background-50">
              <i className="ri-leaf-line text-[12px]"></i>
            </span>
            Our Core Values: Consistency · Caring · Compassion
          </div>
          <div className="flex items-center gap-5 text-xs font-medium text-secondary-900">
            <span className="flex items-center gap-1 cursor-default">
              <i className="ri-global-line text-[14px]"></i> English / Español
            </span>
            <a
              href={COMPANY.phoneHref}
              className="flex items-center gap-1.5 font-semibold text-primary-700 hover:text-accent-600 transition-colors"
            >
              <i className="ri-phone-fill text-[14px]"></i> {COMPANY.phone}
            </a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div
        className={`sticky top-0 z-50 w-full border-b transition-all duration-300 ${
          scrolled
            ? 'border-background-200 bg-background-50/95 backdrop-blur-md'
            : 'border-transparent bg-background-50'
        }`}
      >
        <div className="mx-auto flex h-[72px] max-w-[1280px] items-center justify-between gap-4 px-4 md:px-6">
          <Logo />

          {/* Desktop nav */}
          <nav className="hidden items-center gap-1 lg:flex" aria-label="Main">
            {NAV_ITEMS.map((item) => (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => item.children && handleEnter(item.label)}
                onMouseLeave={handleLeave}
              >
                <a
                  href={item.href}
                  className="flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold text-foreground-800 transition-colors hover:bg-secondary-100 hover:text-primary-700"
                >
                  {item.label}
                  {item.children && <i className="ri-arrow-down-s-line text-[16px]"></i>}
                </a>
                {item.children && openDropdown === item.label && (
                  <div className="absolute left-0 top-full z-50 pt-2">
                    <ul className="w-64 overflow-hidden rounded-lg border border-background-200 bg-background-50 py-2 shadow-lg">
                      {item.children.map((child) => (
                        <li key={child.label}>
                          <a
                            href={child.href}
                            className="block px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-secondary-100 hover:text-primary-700"
                          >
                            {child.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <a
              href="#quote"
              className="whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
            >
              Request A Quote
            </a>
          </div>

          {/* Mobile toggle */}
          <button
            type="button"
            aria-label="Toggle menu"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-primary-700 lg:hidden"
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="border-t border-background-200 bg-background-50 lg:hidden">
            <nav className="mx-auto flex max-w-[1280px] flex-col px-4 py-3" aria-label="Mobile">
              {NAV_ITEMS.map((item) => (
                <div key={item.label} className="border-b border-background-100 last:border-0">
                  <a
                    href={item.href}
                    onClick={() => setMobileOpen(false)}
                    className="block py-3 text-sm font-semibold text-foreground-800"
                  >
                    {item.label}
                  </a>
                  {item.children && (
                    <div className="flex flex-col pb-2 pl-3">
                      {item.children.map((child) => (
                        <a
                          key={child.label}
                          href={child.href}
                          onClick={() => setMobileOpen(false)}
                          className="py-1.5 text-[13px] text-foreground-600"
                        >
                          {child.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <a
                href={COMPANY.phoneHref}
                className="mt-3 flex items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-3 text-sm font-semibold text-background-50"
              >
                <i className="ri-phone-fill"></i> Call {COMPANY.phone}
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}